/**
 * eventLinks.js — where an event card's click goes.
 *
 * There is no in-app page for a single event: the router has /event-group/:id
 * and /venues/:id, but nothing for one event. So an event card's destination is
 * off-site, and which URL we have varies by source:
 *
 *   - Ticketmaster  — `ticket_url` on ~88% of rows (its `external_url` is the
 *                     same link), so "Tickets" is almost always right.
 *   - UitDatabank   — `ticket_url` only when the organiser filled in
 *                     bookingInfo.url (~46% of rows), but `external_url` (the
 *                     uitinvlaanderen.be listing) on 100% of them.
 *
 * Checking only the ticket URL therefore left over half of UitDatabank's events
 * with nowhere to go. Prefer the booking link, fall back to the source's own
 * event page — "learn more" beats a dead end.
 */

/**
 * Whether a stored value is actually a link we can navigate to.
 *
 * Feeds put prose in URL fields. 36 `paris_opendata` rows store things like
 * "Gratuit sur réservation", "En savoir plus", or an email address in
 * `ticket_url`. `sanitizeUrl` passes those through unchanged — its fallback
 * accepts any alphanumeric-initial string with no colon as a relative path — so
 * an unvalidated value becomes an href pointing at a route that doesn't exist.
 * Every one of those rows has a real https:// URL in `external_url`, so
 * validating here is also what lets the fallback rescue them.
 */
export function isNavigableUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value.trim());
}

function ticketUrlOf(activity) {
  const raw = activity && (activity.ticketUrl || activity.ticket_url);
  return isNavigableUrl(raw) ? raw.trim() : null;
}

function externalUrlOf(activity) {
  const raw = activity && (activity.externalUrl || activity.external_url);
  return isNavigableUrl(raw) ? raw.trim() : null;
}

/**
 * The best off-site URL for an event, or null when it has neither.
 * Accepts the search/display shape (`ticketUrl`/`externalUrl`) and the raw API
 * row shape (`ticket_url`/`external_url`).
 */
export function resolveEventUrl(activity) {
  if (!activity) return null;
  return ticketUrlOf(activity) || externalUrlOf(activity) || null;
}

/**
 * Which label the link should carry. A real booking URL earns "Tickets"; a bare
 * listing page only promises information, so it must not claim to sell one —
 * and neither may a `ticket_url` that turned out to be free text, which is why
 * this asks the same navigability question `resolveEventUrl` does.
 */
export function isTicketUrl(activity) {
  return ticketUrlOf(activity) !== null;
}
